import { type ReactNode, type Ref } from 'react'
import cx from 'classnames'
import { TabBar, TabItem } from '@/components/TabBar'
import styles from './HeaderView.module.css'
import { useHoverState } from '@/hooks/element-hooks'
import { Tooltip } from '@/components/Tooltip'
import { Dropdown, DropdownScroll, DropdownTitle } from '@/components/Dropdown'
import { isEmptyNode } from '@/utils/react-util'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { SplitViewToggleButton } from '@/components/SplitView/SplitViewToggleButton'
import { dateFormatters } from '@/utils/date-util'
import { type JobTab } from '@/features/jobs/details/JobDetailsViewModel'

export interface HeaderViewProps {
  onJobListSidebarToggle?: () => unknown
  onJobListHoverChange: (isHovering: boolean) => unknown
  isJobListSidebarVisible: boolean
  jobInfo: ReactNode
  actions: ReactNode
  showTabs: boolean
  currentTab: JobTab
  onTabChange: (tab: JobTab) => unknown
  communicationUnreadCount?: number
}

export function HeaderView({
  onJobListSidebarToggle,
  onJobListHoverChange,
  isJobListSidebarVisible,
  jobInfo,
  actions,
  showTabs,
  currentTab,
  onTabChange,
  communicationUnreadCount,
}: HeaderViewProps) {
  const makeTabProps = (
    tab: JobTab,
    badgeCount?: number,
  ): {
    active?: boolean
    badgeCount?: number
    badgePosition?: 'top' | 'right'
    onPress?: () => unknown
  } => ({
    active: currentTab === tab,
    onPress: () => onTabChange(tab),
    badgeCount,
    badgePosition: 'right',
  })
  const { isHovering: isToggleHovering } = useHoverState(onJobListHoverChange)

  return (
    <div className={styles.root}>
      <JobListSidebarToggle
        onJobListSidebarToggle={onJobListSidebarToggle}
        isToggleHovering={isToggleHovering}
        isJobListSidebarVisible={isJobListSidebarVisible}
      />
      <div className={styles.jobInfo}>
        {jobInfo}
        {actions}
      </div>
      {showTabs && (
        <TabBar>
          <TabItem {...makeTabProps('Details')} testId="details-tab-item-new">
            Details
          </TabItem>
          <TabItem
            {...makeTabProps('Communication', communicationUnreadCount)}
            testId="communication-tab-item-new"
          >
            Chat
          </TabItem>
        </TabBar>
      )}
    </div>
  )
}

export interface JobHeaderInfoProps {
  jobName: string
  isDraftOrPending?: boolean
  formattedStatus: string
  /**
   * When the pending job is allowed to advance, if it is pending.
   */
  advanceAllowedDate?: Date | null
  deadline: Date | null
  daysLeft: string
  /**
   * Summaries shown after the status and due date.
   */
  children?: ReactNode
}

export function JobHeaderInfo({
  jobName,
  isDraftOrPending,
  formattedStatus,
  advanceAllowedDate,
  deadline,
  daysLeft,
  children,
}: JobHeaderInfoProps) {
  return (
    <div>
      <h3 className={styles.jobName}>{jobName}</h3>
      <div>
        <span className={styles.statusLabel}>Status</span>
        <span
          className={cx(
            styles.statusIndicator,
            isDraftOrPending && styles.onboarding,
          )}
        ></span>
        <span className={styles.status} data-test="job-header-status">
          {formattedStatus}
        </span>
        {advanceAllowedDate && (
          <>
            <Divider />
            <span className={styles.statusLabel}>Allowed from</span>{' '}
            <span
              className={styles.status}
              data-test="job-header-advance-allowed"
            >
              {dateFormatters.dateTimeDefault.format(advanceAllowedDate)}
            </span>
          </>
        )}
        {deadline && (
          <>
            <Divider />
            <span className={styles.statusLabel}>Due date</span>
            <Tooltip
              placement="bottom"
              content={dateFormatters.dateTimeDefault.format(deadline)}
            >
              <span className={styles.daysToComplete}>{daysLeft}</span>
            </Tooltip>
          </>
        )}

        {children}
      </div>
    </div>
  )
}

export interface PayoutSituationProps {
  situationType?: string
}

export function PayoutSituation({ situationType }: PayoutSituationProps) {
  switch (situationType) {
    case 'Manual':
      return (
        <>
          <Divider />
          <span className={styles.statusLabel}>Split payout</span>
          <span className={styles.splitPayoutManuallyAllocated}>Allocated</span>
        </>
      )
    case 'Auto':
      return (
        <>
          <Divider />
          <span className={styles.statusLabel}>Split payout</span>
          <span className={styles.splitPayoutAutoAdjusted}>Automated</span>
        </>
      )
    default:
      return null
  }
}

export function NotesSituation() {
  return (
    <>
      <Divider />
      <span className={styles.notesTag}>Limited communication</span>
    </>
  )
}

export interface StatusHistorySummaryProps {
  prefix: string | null
  time: string
  suffix: string | null
}

/**
 * Job time stats depending on the status of the Job.
 */
export function StatusHistorySummary({
  prefix,
  time,
  suffix,
}: StatusHistorySummaryProps) {
  return (
    <>
      <Divider />
      {prefix !== null && (
        <span className={styles.statusHistorySummaryPrefix}>{prefix}</span>
      )}
      <span className={styles.statusHistorySummaryTime}>{time}</span>
      {suffix !== null && (
        <span className={styles.statusHistorySummarySuffix}>{suffix}</span>
      )}
    </>
  )
}

export interface ConsultationSummaryProps {
  isPendingScheduling?: boolean
  pendingSchedulingText: string
  bookedStartTime?: Date
}

/**
 * Displays the booked time of the consultation if available.
 */
export function ConsultationSummary({
  isPendingScheduling,
  pendingSchedulingText,
  bookedStartTime,
}: ConsultationSummaryProps) {
  if (isPendingScheduling) {
    return (
      <>
        <Divider />
        <span className={styles.statusLabel}>{pendingSchedulingText}</span>
      </>
    )
  }

  if (!bookedStartTime) {
    return null
  }

  return (
    <>
      <Divider />
      <span className={styles.statusLabel}>{'Call scheduled '}</span>
      <span className={styles.callTime}>
        {dateFormatters.dateTimeWithZone.format(bookedStartTime)}
      </span>
    </>
  )
}

function Divider() {
  return <span className={styles.divider}>|</span>
}

export interface ActionsProps {
  /**
   * Dropdown items; nothing renders when empty.
   */
  children?: ReactNode
}

export function Actions({ children }: ActionsProps) {
  if (isEmptyNode(children)) {
    return null
  }

  return (
    <div>
      <Dropdown
        autoDismiss
        width="small"
        position="bottom-end"
        button={<Button label="Actions" iconRight="dropdown" />}
        testId="job-actions-dropdown"
        noBottomPadding
      >
        <DropdownTitle>Actions</DropdownTitle>
        <DropdownScroll autoHeightSize>{children}</DropdownScroll>
      </Dropdown>
    </div>
  )
}

interface JobListSidebarToggleProps {
  onJobListSidebarToggle?: () => unknown
  isToggleHovering: boolean
  isJobListSidebarVisible: boolean
  toggleRef?: Ref<HTMLButtonElement>
}

function JobListSidebarToggle({
  onJobListSidebarToggle,
  isToggleHovering,
  isJobListSidebarVisible,
  toggleRef,
}: JobListSidebarToggleProps) {
  if (onJobListSidebarToggle) {
    return (
      <SplitViewToggleButton onPress={onJobListSidebarToggle} ref={toggleRef}>
        {isToggleHovering ? (
          isJobListSidebarVisible ? (
            <Icon name="hover-arrow-highlight" />
          ) : (
            <Icon name="hover-menu-highlight" />
          )
        ) : isJobListSidebarVisible ? (
          <Icon name="hover-arrow" />
        ) : (
          <Icon name="hover-menu" />
        )}
      </SplitViewToggleButton>
    )
  }
  return null
}
